import { useState, type FormEvent } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/dah9n73p14a1h3mr6gd0";

type Status = "idle" | "loading" | "success" | "error";

export default function ContactForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [message, setMessage] = useState("");
  const [count, setCount] = useState(0);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);
    const honeypot = String(formData.get("phone_alt") ?? "").trim();

    if (honeypot) {
      setStatus("success");
      setMessage("已收到你的留言，我们会尽快回复。");
      form.reset();
      setCount(0);
      return;
    }

    setStatus("loading");
    setMessage("");

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "phone_alt") return;
      if (typeof value === "string" && value.trim() !== "") {
        payload.append(key, value);
      }
    });

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        meta?: { message?: string; detail?: string };
        message?: string;
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam =
        typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        setMessage("已收到你的留言，我们会尽快回复。");
        form.reset();
        setCount(0);
      } else {
        setStatus("error");
        setMessage(
          serverMsg && !isSpam && typeof serverMsg === "string"
            ? serverMsg
            : "提交失败，请稍后再试。",
        );
      }
    } catch {
      setStatus("error");
      setMessage("网络异常，请稍后再试。");
    }
  };

  return (
    <form
      id="contact-message"
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg bg-background-50 border border-background-200 p-6 md:p-8"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label
            htmlFor="contact-name"
            className="block text-xs font-label text-foreground-700 mb-2"
          >
            称呼
          </label>
          <input
            id="contact-name"
            type="text"
            name="name"
            required
            placeholder="怎么称呼你"
            className="w-full px-3.5 py-2.5 rounded-md bg-background-100 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors font-label"
          />
        </div>
        <div>
          <label
            htmlFor="contact-email"
            className="block text-xs font-label text-foreground-700 mb-2"
          >
            邮箱
          </label>
          <input
            id="contact-email"
            type="email"
            name="email"
            required
            autoComplete="email"
            placeholder="方便回复的邮箱"
            className="w-full px-3.5 py-2.5 rounded-md bg-background-100 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors font-label"
          />
        </div>
      </div>

      <div className="mt-5">
        <label htmlFor="contact-topic" className="block text-xs font-label text-foreground-700 mb-2">
          留言类型
        </label>
        <select
          id="contact-topic"
          name="topic"
          defaultValue="反馈内容问题"
          className="w-full px-3.5 py-2.5 rounded-md bg-background-100 border border-background-300 text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors font-label cursor-pointer"
        >
          <option value="反馈内容问题">反馈内容问题</option>
          <option value="提供爆料线索">提供爆料线索</option>
          <option value="商务与合作">商务与合作</option>
          <option value="网站使用建议">网站使用建议</option>
          <option value="其他咨询">其他咨询</option>
        </select>
      </div>

      <div className="mt-5">
        <div className="flex items-center justify-between mb-2">
          <label htmlFor="contact-message" className="block text-xs font-label text-foreground-700">
            具体内容
          </label>
          <span className="text-[11px] font-label text-foreground-400">{count} / 500</span>
        </div>
        <textarea
          id="contact-message"
          name="message"
          required
          maxLength={500}
          rows={6}
          onChange={(event) => setCount(event.target.value.length)}
          placeholder="请简单说明你的问题或线索，越具体我们处理得越快（最多 500 字）"
          className="w-full px-3.5 py-2.5 rounded-md bg-background-100 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors font-label resize-none"
        ></textarea>
      </div>

      <div className="form-honeypot-guard" aria-hidden="true">
        <label htmlFor="phone_alt">请勿填写此栏</label>
        <input
          id="phone_alt"
          type="text"
          name="phone_alt"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
        />
      </div>

      <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-4">
        <button
          type="submit"
          disabled={status === "loading"}
          className="inline-flex items-center justify-center gap-1.5 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-label hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60"
        >
          <i className="ri-send-plane-line"></i>
          {status === "loading" ? "提交中…" : "提交留言"}
        </button>
        {status === "success" && message && (
          <p className="text-sm text-secondary-700 font-label flex items-center gap-1.5">
            <i className="ri-checkbox-circle-line"></i>
            {message}
          </p>
        )}
        {status === "error" && message && (
          <p className="text-sm text-primary-600 font-label flex items-center gap-1.5">
            <i className="ri-error-warning-line"></i>
            {message}
          </p>
        )}
      </div>
    </form>
  );
}